import { WalletAccount } from '@exodus/models';
import { getChainDisplayName } from '@passkeys/assets';
import Box from '@passkeys/ui/box';
import Button from '@passkeys/ui/button';
import ChainIcon from '@passkeys/ui/chain-icon';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import TextField from '@passkeys/ui/text-field';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'wouter';

import SendFee from '@/ui/components/send-fee/index.js';
import SendSummary from '@/ui/components/send-summary';
import SendValidation from '@/ui/components/send-validation/send-validation.js';
import Subpage from '@/ui/components/subpage/index.js';
import exodus from '@/ui/exodus.js';
import useAsset from '@/ui/hooks/use-asset.js';
import useAvailableBalance from '@/ui/hooks/use-available-balance.js';
import useFees from '@/ui/hooks/use-fees.js';
import useIsFeeAssetEnough from '@/ui/hooks/use-is-fee-asset-enough.js';
import useNavigate from '@/ui/hooks/use-navigate.js';
import useNft from '@/ui/hooks/use-nft.js';
import useReceiveAddress from '@/ui/hooks/use-receive-address.js';
import useSendValidation from '@/ui/hooks/use-send-validation.js';
import useWalletAccount from '@/ui/hooks/use-wallet-account.js';
import showSendSuccessModal from '@/ui/modals/send-success-modal/index.js';
import { cn } from '@/ui/utils/classnames.js';
import type { ReferrerLocationState } from '@/ui/utils/navigation.js';
import { createNavigateStateOptions } from '@/ui/utils/navigation.js';
import { getChainFromNft } from '@/ui/utils/nfts.js';
import Hero from './components/hero.js';
import createSendOptions from './utils/create-send-options.js';

function CollectibleSendPage() {
  const [toAddress, setToAddress] = useState('');
  const [sending, setSending] = useState(false);

  const { id } = useParams<{ id: string }>();
  const { data: collectible } = useNft({ id: decodeURIComponent(id) });
  if (!collectible) {
    throw new Error(`Invalid collectible ${id}`);
  }

  const navigate = useNavigate();

  const walletAccount = useWalletAccount();

  const assetName = getChainFromNft(collectible);
  const asset = useAsset(assetName);
  if (!asset) {
    throw new Error(`Invalid asset ${assetName}`);
  }

  const nftAsset = useMemo(
    () => ({ ...asset, name: collectible.id }),
    [asset, collectible.id],
  );

  const isSendAll = false;

  const { address: fromAddress } = useReceiveAddress({ assetName });

  const { loading: feesLoading, value: fees } = useFees({
    nft: collectible,
    asset,
    fromAddress,
    isSendAll,
    toAddress,
    walletAccount,
  });

  const isFeeAssetEnough = useIsFeeAssetEnough({
    assetName,
    walletAccount,
    fees,
  });

  const availableBalance = useAvailableBalance({
    assetName,
    fees,
    walletAccount,
  });

  const {
    error,
    warning,
    action: validationAction,
  } = useSendValidation({
    asset: nftAsset,
    toAddress,
    availableBalance,
    fees,
    isFeeAssetEnough,
    withAmount: false,
  });

  const navigateTo = useCallback(
    <S extends Record<string, unknown>>(route: string, state = {} as S) => {
      navigate(
        route,
        createNavigateStateOptions<S & ReferrerLocationState>({
          ...state,
          referrer: 'collectible-send',
        }),
      );
    },
    [navigate],
  );

  const handleSend = useCallback(async () => {
    try {
      setSending(true);

      const options = createSendOptions({
        collectible,
        toAddress,
        walletAccount: WalletAccount.DEFAULT_NAME,

        fees,
      });

      const result = await exodus.transactions.send(options);

      showSendSuccessModal({
        asset,
        collectible,
        toAddress,
        txId: result.txId,
      });

      navigate('/collectibles');
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  }, [asset, collectible, fees, navigate, toAddress]);

  const showValidationMessages = error || warning;

  const loading = !asset || !availableBalance;
  const sendLoading = feesLoading || sending;
  const sendDisabled = !!error || !toAddress;

  return (
    <Subpage title="Send NFT" className="flex h-full flex-col gap-y-8 pb-8">
      <Hero collectible={collectible} />

      <Box className="px-4 py-6">
        <fieldset>
          <Text className="mb-2 leading-none" size={14}>
            Recipient's {getChainDisplayName(asset)} address
          </Text>

          {loading ? (
            <Skeleton height={48} />
          ) : (
            <TextField
              className={cn(
                // TODO: Clean up so that the consumer doesn't have to know about `after`.
                'bg-white after:bg-white theme-dark:bg-neutral-600 theme-dark:after:bg-neutral-600',
                toAddress && 'font-mono',
              )}
              value={toAddress}
              placeholder="Address"
              dataTestId="send-collectible-address-input"
              onChange={setToAddress}
              prefix={
                <ChainIcon name={asset.name} displayName={asset.displayName} />
              }
            />
          )}
        </fieldset>

        <Text className="mb-2 mt-6 leading-none" size={14}>
          Summary
        </Text>

        <SendSummary>
          <SendFee asset={asset} estimatedFee={fees.fee} />
        </SendSummary>
      </Box>

      {showValidationMessages ? (
        <SendValidation
          error={error}
          warning={warning}
          action={
            validationAction
              ? {
                  label: validationAction.label,
                  onClick: () =>
                    navigateTo(
                      validationAction.navigateTo,
                      validationAction.navigateToState,
                    ),
                }
              : undefined
          }
        />
      ) : (
        <Button
          className="mx-4"
          size="large"
          dataTestId="send-collectible-button"
          disabled={sendDisabled}
          loading={sendLoading}
          onClick={handleSend}
        >
          Send
        </Button>
      )}
    </Subpage>
  );
}

export default CollectibleSendPage;
